//@flow
/**
 * VersionChecker - Manages periodic version checking for the app
 * 
 * Features:
 * - Periodic checks (configurable interval)
 * - Window focus detection
 * - Offline detection
 * - Fallback to meta.json if API fails
 */

import { getAppVersion } from './useAppVersion';

const DEFAULT_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes
const MIN_CHECK_INTERVAL = 60 * 1000; // 1 minute minimum

export class VersionChecker {
  dispatch: any;
  checkInterval: any;
  checkIntervalMs: number;
  isOnline: boolean;
  lastCheckTime: number;
  
  constructor(dispatch: any, checkIntervalMs?: number = DEFAULT_CHECK_INTERVAL) {
    this.dispatch = dispatch;
    this.checkInterval = null;
    this.checkIntervalMs = Math.max(checkIntervalMs, MIN_CHECK_INTERVAL);
    this.isOnline = navigator.onLine;
    this.lastCheckTime = 0;
    
    // Listen for online/offline events
    this._setupOnlineDetection();
  }
  
  /**
   * Start periodic version checking
   */
  start(): void {
    if (this.checkInterval) {
      console.warn('[VersionChecker] Already started');
      return;
    }
    
    console.log(`[VersionChecker] Starting with ${this.checkIntervalMs / 1000}s interval`);
    
    // Do initial check
    this._performCheck();
    
    // Setup periodic checks
    this.checkInterval = setInterval(() => {
      this._performCheck();
    }, this.checkIntervalMs);
    
    // Check when window gains focus
    window.addEventListener('focus', this._handleWindowFocus);
    
    // Check when visibility changes (tab becomes active)
    document.addEventListener('visibilitychange', this._handleVisibilityChange);
  }
  
  /**
   * Stop periodic version checking
   */
  stop(): void {
    if (!this.checkInterval) {
      return;
    }
    
    console.log('[VersionChecker] Stopping');
    clearInterval(this.checkInterval);
    this.checkInterval = null;
    
    window.removeEventListener('focus', this._handleWindowFocus);
    document.removeEventListener('visibilitychange', this._handleVisibilityChange);
  }
  
  /**
   * Manually trigger a version check
   */
  checkNow(): void {
    this._performCheck();
  }
  
  /**
   * Setup online/offline detection
   */
  _setupOnlineDetection: () => void = () => {
    window.addEventListener('online', () => {
      console.log('[VersionChecker] Network online');
      this.isOnline = true;
      // Check version when coming back online
      this._performCheck();
    });
    
    window.addEventListener('offline', () => {
      console.log('[VersionChecker] Network offline');
      this.isOnline = false;
    });
  }
  
  /**
   * Handle window focus event
   */
  _handleWindowFocus: () => void = () => {
    const timeSinceLastCheck = Date.now() - this.lastCheckTime;
    
    // Only check if it's been more than 1 minute since last check
    if (timeSinceLastCheck > MIN_CHECK_INTERVAL) {
      console.log('[VersionChecker] Window focused, checking version');
      this._performCheck();
    }
  }
  
  /**
   * Handle visibility change event
   */
  _handleVisibilityChange: () => void = () => {
    if (!document.hidden) {
      const timeSinceLastCheck = Date.now() - this.lastCheckTime;
      
      // Only check if it's been more than 1 minute since last check
      if (timeSinceLastCheck > MIN_CHECK_INTERVAL) {
        console.log('[VersionChecker] Tab visible, checking version');
        this._performCheck();
      }
    }
  }
  
  /**
   * Perform version check if online
   */
  _performCheck: () => void = () => {
    if (!this.isOnline) {
      console.log('[VersionChecker] Skipping check - offline');
      return;
    }
    
    this.lastCheckTime = Date.now();
    
    // Use existing getAppVersion function
    getAppVersion(this.dispatch);
  }
}

export default VersionChecker;
